<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";
import { ApiError, adminApi } from "../api";
import { gameIcons } from "../game-icons";
import type { AdminCatalog, AdminDomain, AdminGame, AdminSyncStatus, ManualArtifactPayload, ProbeSchedule, ProbeStatus, SyncRunStatus, SyncSchedule, VersionSummary } from "../types";

type Tab = "games" | "domains" | "content" | "probe";

const TOKEN_STORAGE_KEY = "game-manifest-index-web-admin-token-v1";

const router = useRouter();
const token = ref(sessionStorage.getItem(TOKEN_STORAGE_KEY) || "");
const authenticated = ref(false);
const catalog = ref<AdminCatalog>({ games: [], domains: [] });
const tab = ref<Tab>("games");
const loading = ref(false);
const error = ref("");
const success = ref("");

const selectedGameId = ref("");
const selectedDomainId = ref("");
const versions = ref<VersionSummary[]>([]);
const selectedVersion = ref("");
const syncStatus = ref<AdminSyncStatus | null>(null);
const probeStatus = ref<ProbeStatus | null>(null);
const probeMode = ref<"normal" | "full">("normal");
const probeSchedule = ref<ProbeSchedule>({ enabled: false, interval_hours: 24, mode: "normal" });
let probePollTimer: number | null = null;
const newGame = ref(false);
const newDomain = ref(false);
const versionDraft = ref({ version: "", client_version: "", source_note: "", artifacts: JSON.stringify([{
  kind: "file", name: "package.zip", part: 1, size: 0,
  urls: [{ url: "https://official.example.com/package.zip", priority: 0, source_kind: "official" }],
}], null, 2) });

let controller: AbortController | null = null;

const gameDraft = ref({ id: "", name: "", sub_name: "", platform: "PC", icon_source: "", is_enabled: true, sort_order: 0 });
const domainDraft = ref({ id: "", game_id: "", kind: "packages", platform: "Windows", capabilities: "packages", adapter: "generic", is_enabled: true, sort_order: 0 });

const selectedGame = computed(() => catalog.value.games.find((item) => item.id === selectedGameId.value) || null);
const selectedDomain = computed(() => catalog.value.domains.find((item) => item.id === selectedDomainId.value) || null);
const gameDomains = computed(() => catalog.value.domains.filter((item) => item.game_id === selectedGameId.value));
const selectedVersionSummary = computed(() => versions.value.find((item) => item.version === selectedVersion.value) || null);
const gameIconPreview = computed(() => resolveGameIcon(gameDraft.value.id, gameDraft.value.icon_source));

function resolveGameIcon(id: string, source?: string): string | undefined {
  const clean = source?.trim();
  if (clean?.startsWith("builtin:")) return gameIcons[clean.slice("builtin:".length)] || gameIcons[id];
  return clean || gameIcons[id];
}

function useFallbackIcon(event: Event, id: string): void {
  const image = event.currentTarget as HTMLImageElement;
  const fallback = gameIcons[id];
  if (fallback && image.src !== fallback) image.src = fallback;
  else image.hidden = true;
}

function abortAndCreate(): AbortController {
  controller?.abort();
  controller = new AbortController();
  return controller;
}

function showError(reason: unknown): void {
  if (reason instanceof DOMException && reason.name === "AbortError") return;
  error.value = reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : String(reason);
  success.value = "";
  if (reason instanceof ApiError && reason.status === 401) authenticated.value = false;
}

async function withLoading(action: (signal: AbortSignal) => Promise<void>): Promise<void> {
  loading.value = true;
  error.value = "";
  success.value = "";
  const request = abortAndCreate();
  try {
    await action(request.signal);
  } catch (reason) {
    showError(reason);
  } finally {
    loading.value = false;
  }
}

async function loadCatalog(signal?: AbortSignal): Promise<void> {
  catalog.value = await adminApi.catalog(token.value.trim(), signal);
  selectedGameId.value = catalog.value.games.some((item) => item.id === selectedGameId.value)
    ? selectedGameId.value : catalog.value.games[0]?.id || "";
  selectedDomainId.value = catalog.value.domains.some((item) => item.id === selectedDomainId.value)
    ? selectedDomainId.value : gameDomains.value[0]?.id || catalog.value.domains[0]?.id || "";
  if (catalog.value.games.length) selectGame(selectedGameId.value);
}

async function login(): Promise<void> {
  if (!token.value.trim()) {
    error.value = "请输入管理密码。";
    return;
  }
  await withLoading(async (signal) => {
    await loadCatalog(signal);
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token.value.trim());
    authenticated.value = true;
    syncStatus.value = await adminApi.syncStatus(token.value.trim(), signal).catch(() => null);
  });
}

function logout(): void {
  stopProbePolling();
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  token.value = "";
  authenticated.value = false;
  catalog.value = { games: [], domains: [] };
  versions.value = [];
  syncStatus.value = null;
}

async function refreshSyncStatus(): Promise<void> {
  await withLoading(async (signal) => {
    syncStatus.value = await adminApi.syncStatus(token.value.trim(), signal);
  });
}

async function loadProbeStatus(): Promise<void> {
  try {
    probeStatus.value = await adminApi.probeStatus(token.value.trim());
  } catch (reason) {
    if (!(reason instanceof DOMException && reason.name === "AbortError")) showError(reason);
  }
}

function startProbePolling(): void {
  stopProbePolling();
  probePollTimer = window.setInterval(async () => {
    if (!authenticated.value || tab.value !== "probe") return;
    await loadProbeStatus();
  }, 2500);
}

function stopProbePolling(): void {
  if (probePollTimer !== null) {
    window.clearInterval(probePollTimer);
    probePollTimer = null;
  }
}

async function openProbe(): Promise<void> {
  tab.value = "probe";
  await loadProbeStatus();
  try {
    probeSchedule.value = await adminApi.probeSchedule(token.value.trim());
  } catch (reason) {
    if (!(reason instanceof DOMException && reason.name === "AbortError")) showError(reason);
  }
  startProbePolling();
}

async function saveProbeSchedule(): Promise<void> {
  await withLoading(async (signal) => {
    probeSchedule.value = await adminApi.saveProbeSchedule(probeSchedule.value, token.value, signal);
    success.value = "探活计划已保存。请配置 Proma 定时任务或系统计划任务按间隔触发。";
  });
}

async function runProbe(): Promise<void> {
  await withLoading(async (signal) => {
    await adminApi.probeRun(probeMode.value, token.value, signal);
    await loadProbeStatus();
  });
}

const syncRunStatus = ref<SyncRunStatus | null>(null);

async function loadSyncRunStatus(): Promise<void> {
  if (!authenticated.value) return;
  syncRunStatus.value = await adminApi.syncRunStatus(token.value.trim());
}

async function runSync(): Promise<void> {
  await withLoading(async (signal) => {
    await adminApi.syncRun(token.value, signal);
    await loadSyncRunStatus();
    success.value = "全量采集已在后台启动。完成后 TG 会收到结果通知。";
  });
}

const syncSchedule = ref<SyncSchedule>({ enabled: true, times: ["04:45", "14:00"] });

async function loadSyncSchedule(): Promise<void> {
  if (!authenticated.value) return;
  syncSchedule.value = await adminApi.syncSchedule(token.value.trim());
}

async function saveSyncSchedule(): Promise<void> {
  await withLoading(async (signal) => {
    syncSchedule.value = await adminApi.saveSyncSchedule(syncSchedule.value, token.value, signal);
    success.value = "采集计划已保存。调度器每 15 分钟检查一次，按配置时间触发采集。";
  });
}

function scheduleTimesText(): string {
  const times = syncSchedule.value?.times || [];
  return times.length ? times.join(" / ") : "未配置";
}

function probeStuckRows(): string[] {
  const log = probeStatus.value?.log || [];
  const stuck: string[] = [];
  for (const line of log) {
    const m = line.match(/^\[probe\] \(\d+\/\d+\) probing (.+)$/);
    if (m) {
      const url = m[1];
      const hasDone = log.some((item) => item.includes(`done `) && item.includes(url));
      if (!hasDone) {
        stuck.push(url.length > 90 ? url.slice(0, 90) + "…" : url);
      }
    }
  }
  return stuck.slice(-8);
}

function probeProgress(): string {
  const log = probeStatus.value?.log || [];
  const m = log.find((line) => /^\[probe\] \(/.test(line));
  return m ? m.replace(/^\[probe\] /, "") : "准备中…";
}

function openGames(): void {
  tab.value = "games";
  selectGame(selectedGameId.value);
}

function selectGame(id: string): void {
  selectedGameId.value = id;
  newGame.value = false;
  const item = catalog.value.games.find((row) => row.id === id);
  if (item) {
    gameDraft.value = {
      id: item.id, name: item.name, sub_name: item.sub_name, platform: item.platform,
      icon_source: item.icon_source, is_enabled: item.is_enabled, sort_order: item.sort_order ?? 0,
    };
  }
  const available = catalog.value.domains.filter((row) => row.game_id === id);
  if (!available.some((row) => row.id === selectedDomainId.value)) selectedDomainId.value = available[0]?.id || "";
}

function startGame(): void {
  newGame.value = true;
  gameDraft.value = { id: "", name: "", sub_name: "", platform: "PC", icon_source: "", is_enabled: true, sort_order: catalog.value.games.length * 10 };
}

async function saveGame(): Promise<void> {
  await withLoading(async (signal) => {
    const payload = { ...gameDraft.value };
    if (newGame.value) await adminApi.createGame(payload, token.value, signal);
    else await adminApi.updateGame(payload.id, payload, token.value, signal);
    await loadCatalog(signal);
    selectGame(payload.id);
    success.value = newGame.value ? "游戏已创建。" : "游戏设置已保存。";
    newGame.value = false;
  });
}

async function removeGame(): Promise<void> {
  if (!newGame.value && !confirm("仅空游戏可删除；有模块的游戏请先停用。继续？")) return;
  await withLoading(async (signal) => {
    await adminApi.deleteGame(gameDraft.value.id, token.value, signal);
    await loadCatalog(signal);
    success.value = "空游戏已删除。";
  });
}

function selectDomain(id: string): void {
  selectedDomainId.value = id;
  newDomain.value = false;
  const item = catalog.value.domains.find((row) => row.id === id);
  if (item) {
    domainDraft.value = {
      id: item.id, game_id: item.game_id, kind: item.kind, platform: item.platform,
      capabilities: item.capabilities.join(", "), adapter: item.adapter,
      is_enabled: item.is_enabled, sort_order: item.sort_order ?? 0,
    };
  }
}

function startDomain(): void {
  newDomain.value = true;
  domainDraft.value = {
    id: "", game_id: selectedGameId.value, kind: "packages", platform: "Windows",
    capabilities: "packages", adapter: "generic", is_enabled: true, sort_order: gameDomains.value.length * 10,
  };
}

function openDomains(): void {
  tab.value = "domains";
  const target = gameDomains.value.find((item) => item.id === selectedDomainId.value) || gameDomains.value[0];
  if (target) selectDomain(target.id);
  else startDomain();
}

function selectModuleGame(id: string): void {
  selectGame(id);
  const first = gameDomains.value[0];
  if (first) selectDomain(first.id);
  else startDomain();
}

function formatSyncTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(date);
}

function domainBadge(item: AdminDomain): string {
  if (item.kind === "resources") return "RES";
  if (item.kind === "chunks") return "CHK";
  if (item.kind === "apk") return "APK";
  if (item.kind === "files") return "FILE";
  if (item.kind === "patches") return "PATCH";
  return item.platform.toLowerCase().includes("android") ? "APK" : "PC";
}

function domainPayload(): Partial<AdminDomain> {
  return {
    ...domainDraft.value,
    capabilities: domainDraft.value.capabilities.split(",").map((item) => item.trim()).filter(Boolean),
  };
}

async function saveDomain(): Promise<void> {
  await withLoading(async (signal) => {
    const payload = domainPayload();
    if (newDomain.value) await adminApi.createDomain(payload, token.value, signal);
    else await adminApi.updateDomain(payload.id || "", payload, token.value, signal);
    await loadCatalog(signal);
    selectDomain(payload.id || "");
    success.value = newDomain.value ? "模块已创建。" : "模块设置已保存。";
    newDomain.value = false;
  });
}

async function removeDomain(): Promise<void> {
  if (!confirm("仅没有版本和候选记录的空模块可删除；其他模块请停用。继续？")) return;
  await withLoading(async (signal) => {
    await adminApi.deleteDomain(domainDraft.value.id, token.value, signal);
    await loadCatalog(signal);
    success.value = "空模块已删除。";
  });
}

async function openContent(id = selectedDomainId.value): Promise<void> {
  selectedDomainId.value = id;
  selectDomain(id);
  tab.value = "content";
  await withLoading(async (signal) => {
    const response = await adminApi.versions(id, token.value, signal);
    versions.value = response.items;
    selectedVersion.value = versions.value[0]?.version || "";
  });
}

async function toggleVersionVisibility(): Promise<void> {
  const current = selectedVersionSummary.value;
  if (!selectedDomainId.value || !selectedVersion.value || !current) return;
  await withLoading(async (signal) => {
    await adminApi.setVersionVisibility(selectedDomainId.value, selectedVersion.value, !current.is_visible, token.value, signal);
    await openContent(selectedDomainId.value);
    success.value = current.is_visible ? "版本已隐藏。" : "版本已公开。";
  });
}

async function addVersion(): Promise<void> {
  if (!selectedDomainId.value || !versionDraft.value.version.trim()) return;
  let artifacts: ManualArtifactPayload[];
  try {
    const parsed = JSON.parse(versionDraft.value.artifacts);
    if (!Array.isArray(parsed) || !parsed.length) throw new Error("empty");
    artifacts = parsed as ManualArtifactPayload[];
  } catch {
    error.value = "Artifacts JSON 无效或为空。";
    return;
  }
  await withLoading(async (signal) => {
    await adminApi.addVersion(selectedDomainId.value, {
      version: versionDraft.value.version.trim(),
      client_version: versionDraft.value.client_version.trim() || undefined,
      source_note: versionDraft.value.source_note.trim() || undefined,
      attributes: {},
      artifacts,
    }, token.value, signal);
    versionDraft.value = { version: "", client_version: "", source_note: "", artifacts: versionDraft.value.artifacts };
    await openContent(selectedDomainId.value);
    success.value = "版本已添加。";
  });
}

onBeforeUnmount(() => { controller?.abort(); stopProbePolling(); });
</script>

<template>
  <div class="admin-app">
    <nav class="admin-nav">
      <div class="admin-brand"><span class="brand-logo"><b>A</b></span><div><strong>归档管理</strong><small>Game Manifest Index</small></div></div>
      <div v-if="authenticated" class="admin-nav-sections">
        <div class="side-section"><span class="side-label">管理工作区</span><button class="side-link" :class="{ active: tab === 'games' }" @click="openGames"><span>游戏入口</span><small>名称、排序与公开状态</small></button><button class="side-link" :class="{ active: tab === 'domains' }" @click="openDomains"><span>数据模块</span><small>平台、能力与 Adapter</small></button><button class="side-link" :class="{ active: tab === 'content' }" @click="openContent()"><span>版本内容</span><small>可见性与手动版本</small></button><button class="side-link" :class="{ active: tab === 'probe' }" @click="openProbe"><span>探活管理</span><small>APK URL 探活与日志</small></button></div>
      </div>
      <div class="side-foot"><span>管理员写入通道</span><button v-if="authenticated" @click="logout">退出管理</button></div>
    </nav>
    <main class="admin-main">
      <header class="admin-topbar"><div><p class="kicker">Administration</p><h1>统一归档管理</h1></div><div class="admin-header-actions"><button class="admin-link" @click="router.push('/')">返回归档</button></div></header>

      <form v-if="!authenticated" class="admin-login" @submit.prevent="login"><div class="admin-login-card"><p class="kicker">Admin token</p><h2>管理密码</h2><input v-model="token" type="password" placeholder="输入管理 token" autocomplete="current-password" /><button class="admin-submit" :disabled="loading">{{ loading ? '验证中…' : '登录' }}</button></div></form>

      <template v-else>
        <div v-if="error" class="admin-error">{{ error }}</div>
        <div v-if="success" class="admin-success">{{ success }}</div>

        <section v-if="syncStatus" class="admin-sync-card"><div class="sync-current-head"><span class="sync-pulse" aria-hidden="true"></span><div><p class="kicker">Sync Status</p><h2>数据采集与同步状态</h2></div></div><div class="admin-sync-grid"><div><span>当前快照</span><strong>{{ syncStatus.latest_snapshot || '—' }}</strong></div><div><span>最近刷新</span><strong>{{ syncStatus.latest_refresh?.status || '—' }}</strong><small v-if="syncStatus.latest_refresh?.completed_at">{{ formatSyncTime(syncStatus.latest_refresh.completed_at) }}</small></div><div><span>失败家族</span><strong>{{ Object.keys(syncStatus.latest_refresh?.failures || {}).length ? Object.keys(syncStatus.latest_refresh!.failures!).join(', ') : '无' }}</strong></div><button class="admin-secondary" :disabled="loading" @click="refreshSyncStatus">刷新状态</button></div></section>

        <section v-if="tab === 'games'" class="admin-workspace">
          <aside class="admin-index">
            <div class="admin-index-head"><div><span>Games</span><strong>{{ catalog.games.length }} 个入口</strong></div><button @click="startGame">＋ 新游戏</button></div>
            <button v-for="item in catalog.games" :key="item.id" class="admin-game-row" :class="{ active: item.id === selectedGameId }" @click="selectGame(item.id)">
              <span class="admin-game-icon"><img v-if="resolveGameIcon(item.id, item.icon_source)" :class="{ 'endfield-icon': item.id === 'endfield' }" :src="resolveGameIcon(item.id, item.icon_source)" :alt="item.name" @error="useFallbackIcon($event, item.id)" /><b v-else>{{ item.id.slice(0, 1).toUpperCase() }}</b><i :class="{ off: !item.is_enabled }"></i></span>
              <span><strong>{{ item.name }}</strong><small>{{ item.id }} · {{ item.version_count }} 个版本</small></span><em>{{ item.sort_order }}</em>
            </button>
          </aside>
          <form class="admin-editor" @submit.prevent="saveGame">
            <div class="admin-editor-head">
              <div class="admin-game-heading"><span class="admin-icon-preview"><img v-if="gameIconPreview" :class="{ 'endfield-icon': gameDraft.id === 'endfield' }" :src="gameIconPreview" :alt="gameDraft.name" @error="useFallbackIcon($event, gameDraft.id)" /><b v-else>{{ gameDraft.id.slice(0, 1).toUpperCase() || '?' }}</b></span><div><p class="kicker">Game identity</p><h2>{{ newGame ? '创建游戏入口' : gameDraft.name }}</h2></div></div>
              <span class="admin-state" :class="{ off: !gameDraft.is_enabled }">{{ gameDraft.is_enabled ? '公开显示' : '已停用' }}</span>
            </div>
            <div class="admin-grid">
              <label><span>游戏 ID</span><input v-model="gameDraft.id" :disabled="!newGame" required /></label>
              <label><span>显示顺序</span><input v-model.number="gameDraft.sort_order" type="number" /></label>
              <label><span>中文名称</span><input v-model="gameDraft.name" required /></label>
              <label><span>英文 / 副标题</span><input v-model="gameDraft.sub_name" required /></label>
              <label><span>默认平台</span><input v-model="gameDraft.platform" required /></label>
              <label class="admin-switch"><input v-model="gameDraft.is_enabled" type="checkbox" /><span>在公共导航中显示</span></label>
              <label class="wide admin-icon-source"><span>图标数据源</span><input v-model="gameDraft.icon_source" placeholder="builtin:nte、/assets/icon.png 或 https://…" /><small>留空时使用项目内置图标；支持内置标识、站内绝对路径或 HTTPS URL。</small></label>
            </div>
            <div class="admin-form-actions"><button v-if="!newGame" type="button" class="danger" @click="removeGame">删除空游戏</button><button class="admin-submit" :disabled="loading">保存游戏</button></div>
          </form>
        </section>

        <section v-else-if="tab === 'domains'" class="admin-workspace">
          <aside class="admin-index admin-module-index">
            <div class="admin-index-head"><div><span>Modules · {{ catalog.domains.length }} total</span><strong>{{ gameDomains.length }} 个模块</strong></div><button @click="startDomain">＋ 新模块</button></div>
            <label class="admin-module-game"><span class="admin-game-icon"><img v-if="selectedGame && resolveGameIcon(selectedGame.id, selectedGame.icon_source)" :class="{ 'endfield-icon': selectedGame.id === 'endfield' }" :src="resolveGameIcon(selectedGame.id, selectedGame.icon_source)" :alt="selectedGame.name" @error="useFallbackIcon($event, selectedGame.id)" /></span><span><small>当前游戏</small><select :value="selectedGameId" @change="selectModuleGame(($event.target as HTMLSelectElement).value)"><option v-for="game in catalog.games" :key="game.id" :value="game.id">{{ game.name }}</option></select></span></label>
            <button v-for="item in gameDomains" :key="item.id" class="admin-module-row" :class="{ active: item.id === selectedDomainId }" @click="selectDomain(item.id)"><span class="admin-domain-badge">{{ domainBadge(item) }}<i :class="{ off: !item.is_enabled }"></i></span><span><strong>{{ item.kind }} · {{ item.platform }}</strong><small>{{ item.id }} · {{ item.version_count }} 个版本</small></span><em>{{ item.sort_order }}</em></button>
            <div v-if="!gameDomains.length" class="admin-empty">该游戏还没有数据模块。</div>
          </aside>
          <form class="admin-editor" @submit.prevent="saveDomain">
            <div class="admin-editor-head"><div><p class="kicker">Archive module</p><h2>{{ newDomain ? '创建数据模块' : domainDraft.id || '选择模块' }}</h2></div><button v-if="!newDomain && domainDraft.id" type="button" class="admin-open-content" @click="openContent(domainDraft.id)">管理内容 →</button></div>
            <div class="admin-grid">
              <label><span>模块 ID</span><input v-model="domainDraft.id" :disabled="!newDomain" required /></label>
              <label><span>所属游戏</span><select v-model="domainDraft.game_id" :disabled="!newDomain"><option v-for="game in catalog.games" :key="game.id" :value="game.id">{{ game.name }}</option></select></label>
              <label><span>模块类型</span><select v-model="domainDraft.kind"><option v-for="kind in ['packages','files','patches','chunks','apk','resources','mixed']" :key="kind">{{ kind }}</option></select></label>
              <label><span>平台</span><input v-model="domainDraft.platform" required /></label>
              <label><span>Adapter</span><input v-model="domainDraft.adapter" required /></label>
              <label><span>显示顺序</span><input v-model.number="domainDraft.sort_order" type="number" /></label>
              <label class="wide"><span>功能模式（逗号分隔）</span><input v-model="domainDraft.capabilities" placeholder="packages, files, patches" required /></label>
              <label class="admin-switch wide"><input v-model="domainDraft.is_enabled" type="checkbox" /><span>在该游戏页面显示此模块</span></label>
            </div>
            <div class="admin-form-actions"><button v-if="!newDomain && domainDraft.id" type="button" class="danger" @click="removeDomain">删除空模块</button><button class="admin-submit" :disabled="loading || !domainDraft.id">保存模块</button></div>
          </form>
        </section>

        <section v-else-if="tab === 'probe'" class="admin-probe">
          <div class="admin-probe-head"><div><p class="kicker">Manual sync</p><h2>手动采集</h2><p>在后台触发全量采集管线（采集 → 发布快照 → 刷新 → TG 通知）。与每日 04:45 的定时采集等效。</p></div></div>
          <div class="admin-probe-controls">
            <button class="admin-submit" :disabled="loading || syncRunStatus?.running" @click="runSync">{{ syncRunStatus?.running ? '采集中…' : '开始采集' }}</button>
          </div>
          <div v-if="syncRunStatus" class="admin-probe-state">
            <div><span>采集状态</span><strong :class="{ running: syncRunStatus.running }">{{ syncRunStatus.running ? '运行中' : syncRunStatus.status === 'finished' ? '已完成' : '空闲' }}</strong></div>
            <div v-if="syncRunStatus.exit_code !== null && syncRunStatus.exit_code !== undefined"><span>退出码</span><strong>{{ syncRunStatus.exit_code }}</strong></div>
            <div v-if="syncRunStatus.result?.updates_text"><span>更新摘要</span><strong class="progress">{{ syncRunStatus.result.updates_text.slice(0, 300) }}</strong></div>
          </div>
          <div class="admin-probe-log"><div class="admin-probe-log-head"><div><p class="kicker">Sync log</p><h2>采集日志</h2></div><button class="admin-secondary" :disabled="loading" @click="loadSyncRunStatus">刷新</button></div><pre><code>{{ syncRunStatus?.result?.log_tail || '尚无采集日志' }}</code></pre></div>

          <div class="admin-probe-schedule">
            <div><p class="kicker">Scheduled sync</p><h2>定时采集计划</h2><p>每天按配置的时间点自动采集（北京时间 HH:MM）。服务器调度器每 15 分钟检查一次；保存后立即生效。</p></div>
            <label class="admin-switch"><input v-model="syncSchedule.enabled" type="checkbox" /><span>启用定时采集（当前：{{ syncSchedule.enabled ? '已启用' : '已停用' }}）</span></label>
            <div class="admin-probe-controls">
              <label><span>时间点 1</span><input v-model="syncSchedule.times[0]" type="time" /></label>
              <label><span>时间点 2</span><input v-model="syncSchedule.times[1]" type="time" /></label>
              <button class="admin-submit" :disabled="loading" @click="saveSyncSchedule">保存采集计划</button>
            </div>
            <p class="kicker" style="margin-top:8px">当前计划：{{ syncSchedule.enabled ? scheduleTimesText() : '（已停用）' }}</p>
          </div>

          <div class="admin-probe-head"><div><p class="kicker">Availability probe</p><h2>APK URL 探活</h2><p>对 Android APK 的官方 CDN URL 执行可用性探测。正常轮只重新探测超过 TTL（20 小时）的 URL；全量轮强制重新探测全部。</p></div></div>
          <div class="admin-probe-controls">
            <label><span>模式</span><select v-model="probeMode"><option value="normal">正常轮（TTL 20h）</option><option value="full">全量轮（全部 URL）</option></select></label>
            <button class="admin-submit" :disabled="loading || probeStatus?.status === 'running'" @click="runProbe">{{ probeStatus?.status === 'running' ? '探活中…' : '开始探活' }}</button>
          </div>
          <div class="admin-probe-schedule">
            <div><p class="kicker">Scheduled probe</p><h2>定时探活</h2><p>保存计划后，用 Proma 定时任务或系统计划任务按间隔触发；后台本身不常驻定时器。</p></div>
            <div class="admin-probe-schedule-fields">
              <label class="admin-switch"><input v-model="probeSchedule.enabled" type="checkbox" /><span>启用定时探活</span></label>
              <label><span>间隔（小时）</span><input v-model.number="probeSchedule.interval_hours" type="number" min="1" max="168" /></label>
              <label><span>默认模式</span><select v-model="probeSchedule.mode"><option value="normal">正常轮</option><option value="full">全量轮</option></select></label>
              <button class="admin-submit" :disabled="loading" @click="saveProbeSchedule">保存计划</button>
            </div>
          </div>
          <div v-if="probeStatus" class="admin-probe-state">
            <div><span>状态</span><strong :class="{ running: probeStatus.status === 'running' }">{{ probeStatus.status === 'running' ? '运行中' : probeStatus.status === 'finished' ? '已完成' : '空闲' }}</strong></div>
            <div><span>模式</span><strong>{{ probeStatus.mode === 'full' ? '全量轮' : '正常轮' }}</strong></div>
            <div v-if="probeStatus.status === 'running'"><span>当前进度</span><strong class="progress">{{ probeProgress() }}</strong></div>
            <div v-if="probeStuckRows().length"><span>疑似卡住</span><strong class="stuck">{{ probeStuckRows().join(' | ') }}</strong></div>
          </div>
          <div class="admin-probe-log"><div class="admin-probe-log-head"><div><p class="kicker">Probe log</p><h2>探测日志</h2></div><button class="admin-secondary" :disabled="loading" @click="loadProbeStatus">刷新</button></div><pre><code>{{ (probeStatus?.log || []).slice(-200).join('\n') }}
') }}
') }}
') }}</code></pre></div>
        </section>

        <section v-else class="admin-content-layout">
          <aside class="admin-content-nav">
            <label><span>游戏</span><select v-model="selectedGameId" @change="selectGame(selectedGameId)"><option v-for="game in catalog.games" :key="game.id" :value="game.id">{{ game.name }}</option></select></label>
            <label><span>模块</span><select v-model="selectedDomainId" @change="openContent(selectedDomainId)"><option v-for="domain in gameDomains" :key="domain.id" :value="domain.id">{{ domain.kind }} · {{ domain.platform }}</option></select></label>
            <label><span>版本</span><select v-model="selectedVersion"><option v-for="item in versions" :key="item.version" :value="item.version">{{ item.version }} · {{ item.artifact_count }} 项</option></select></label>
            <div class="admin-version-state"><span>当前 Revision</span><strong>#{{ selectedVersionSummary?.current_revision_id || '—' }}</strong><small>内容修改会创建新 revision</small></div>
            <button class="admin-secondary" :disabled="!selectedVersion" @click="toggleVersionVisibility">{{ selectedVersionSummary?.is_visible ? '切换为隐藏' : '切换为公开' }}</button>
          </aside>
          <section class="admin-add-version">
            <div><p class="kicker">Manual discovery</p><h2>添加自行发现的版本</h2></div>
            <div class="admin-grid">
              <label><span>版本</span><input v-model="versionDraft.version" placeholder="例如 76.1.0" /></label>
              <label><span>客户端版本</span><input v-model="versionDraft.client_version" placeholder="默认同版本" /></label>
              <label class="wide"><span>来源说明</span><input v-model="versionDraft.source_note" placeholder="官方启动器、公告或 CDN 入口" /></label>
              <label class="wide"><span>Artifacts JSON</span><textarea v-model="versionDraft.artifacts" spellcheck="false" rows="6"></textarea></label>
            </div>
            <button class="admin-submit" :disabled="loading || !selectedDomainId || !versionDraft.version.trim()" @click="addVersion">保存新版本</button>
          </section>
        </section>
      </template>
    </main>
  </div>
</template>
